import { createMutationHook, createQueryHook } from "../factory";

export const caseKeys = {
  all: ["cases"] as const,
  lists: () => [...caseKeys.all, "list"] as const,
  list: <T>(filters: T) => [...caseKeys.lists(), filters] as const,
  details: () => [...caseKeys.all, "detail"] as const,
  detail: ({ id, withClaim }: { id: string; withClaim?: boolean }) =>
    [...caseKeys.details(), id, { withClaim: withClaim ?? false }] as const,
};

export const threadKeys = {
  all: ["threads"] as const,
  lists: () => [...threadKeys.all, "list"] as const,
  list: <T>(filters: T) => [...threadKeys.lists(), filters] as const,
  details: () => [...threadKeys.all, "detail"] as const,
  detail: <T>(filters: T) => [...threadKeys.details(), filters],
};

const savedPromptKeys = {
  all: ["savedPrompts"] as const,
  lists: () => [...savedPromptKeys.all, "list"] as const,
  list: <T>(filters: T) => [...savedPromptKeys.lists(), filters] as const,
};

export const agentKeys = {
  all: ["agents"] as const,
  lists: () => [...agentKeys.all, "list"] as const,
  list: <T>(filters: T) => [...agentKeys.lists(), filters] as const,
  details: () => [...agentKeys.all, "detail"] as const,
  detail: ({ id }: { id: string }) => [...agentKeys.details(), id] as const,
  types: () => [...agentKeys.all, "types"] as const,
};

export const cases = {
  getAllCases: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getAllCases,
    queryKeyFn: caseKeys.list,
  }),
  getCasesConnection: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getCasesConnection,
    queryKeyFn: caseKeys.list,
  }),
  getClaimCases: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getClaimCases,
    queryKeyFn: caseKeys.list,
  }),
  getCase: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getCase,
    queryKeyFn: caseKeys.detail,
  }),
  getThreads: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getThreads,
    queryKeyFn: threadKeys.list,
  }),
  getThread: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getThread,
    queryKeyFn: threadKeys.detail,
  }),
  createCase: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.createCase,
    mutationKey: ["cases", "createCase"],
  }),
  updateCase: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.updateCase,
    mutationKey: ["cases", "updateCase"],
  }),
  deleteCase: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.deleteCase,
    mutationKey: ["cases", "deleteCase"],
  }),
  prompt: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.prompt,
    mutationKey: ["cases", "prompt"],
  }),
  createThread: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.createThread,
    mutationKey: ["cases", "createThread"],
  }),
  getAllSavedPrompts: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getAllSavedPrompts,
    queryKeyFn: savedPromptKeys.list,
  }),
  createSavedPrompt: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.createSavedPrompt,
    mutationKey: ["cases", "createSavedPrompt"],
  }),
  deleteSavedPrompt: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.deleteSavedPrompt,
    mutationKey: ["cases", "deleteSavedPrompt"],
  }),
  resetSavedPrompt: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.resetSavedPrompt,
    mutationKey: ["cases", "resetSavedPrompt"],
  }),
  ratePromptResponse: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.ratePromptResponse,
    mutationKey: ["cases", "ratePromptResponse"],
  }),
  getAgents: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getAgents,
    queryKeyFn: agentKeys.list,
  }),
  getAgent: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getAgent,
    queryKeyFn: agentKeys.detail,
  }),
  createAgentDemo: createMutationHook({
    methodSelector: (alafffiaClient) => alafffiaClient.cases.createAgentDemo,
    mutationKey: ["cases", "createAgentDemo"],
  }),
  createAgentMessageRevision: createMutationHook({
    methodSelector: (alafffiaClient) =>
      alafffiaClient.cases.createAgentMessageRevision,
    mutationKey: ["cases", "createAgentMessageRevision"],
  }),
  moveCasesToQueue: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.moveCasesToQueue,
    mutationKey: ["cases", "moveCasesToQueue"],
  }),
  acceptAgentMessage: createMutationHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.acceptAgentMessage,
    mutationKey: ["cases", "acceptAgentMessage"],
  }),
  unsetAgentMessageUserValidation: createMutationHook({
    methodSelector: (alaffiaClient) =>
      alaffiaClient.cases.unsetAgentMessageUserValidation,
    mutationKey: ["cases", "unsetAgentMessageUserValidation"],
  }),
  getAgentTypes: createQueryHook({
    methodSelector: (alaffiaClient) => alaffiaClient.cases.getAgentTypes,
    queryKeyFn: agentKeys.types,
  }),
};
